import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { AlertService } from '../../../services/alert.service';
import { ToastService } from '../../../services/toast.service';
import { AuthService } from '../../../services/auth.service';
import { LoadingService } from '../../../services/loading.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidationUtil } from '../../../utils/validation';
import { IonInput, Platform } from '@ionic/angular';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ContentService } from 'src/app/services/content.service';
import { ReceivedMessage, RegisterReceiver, SmsRetriever } from 'capacitor-sms-retriever';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { ONGOING_LOGIN_SESSION_INFO } from 'src/app/pages/authentication/home/home.page';

@Component({
  selector: 'app-verify-sign-up',
  templateUrl: './verify-sign-up.page.html',
  styleUrls: ['./verify-sign-up.page.scss', '../authentication.scss'],
})
export class VerifySignUpPage implements OnInit, AfterViewInit, OnDestroy {

  title = 'Enter Code';
  verifyCodeForm: FormGroup;
  fieldNames = {
    code: 'SMS Code',
  };
  phone!: string;
  showResendButton = true;
  @ViewChild('code') code!: IonInput;
  smsReceiverResponse: RegisterReceiver | undefined;
  showAdditionalContent = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private docTitle: Title,
    private formBuilder: FormBuilder,
    private alertProvider: AlertService,
    private toastProvider: ToastService,
    private amplifyProvider: AuthService,
    private loadingProvider: LoadingService,
    private analyticsService: AnalyticsService,
    private contentService: ContentService,
    private platform: Platform,
  ) {
    this.analyticsService.tagRoute('unauthenticated/verifysignup');
    this.docTitle.setTitle(this.title);
    this.verifyCodeForm = this.formBuilder.group({
      code: ['', [ValidationUtil.isRequired]]
    });
    this.registerSmsRetriever();
  }

  async registerSmsRetriever() {
    if (Capacitor.isNativePlatform() && this.platform.is('android')) {
      if (this.smsReceiverResponse?.isRegistered) {
        await SmsRetriever.removeSmsReceiver();
      }
  
      this.smsReceiverResponse = await SmsRetriever.startSmsReceiver();
  
      if (this.smsReceiverResponse?.isRegistered) {
        const smsReceiveListener = await SmsRetriever.addListener('onSmsReceive', (receivedMessage: ReceivedMessage) => {
          if (!receivedMessage.error) {
            this.processSms(receivedMessage.message || "");
            smsReceiveListener.remove();
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    console.log('on destroy running');
    if (Capacitor.isNativePlatform()) {
      SecureStoragePlugin.set({ key: ONGOING_LOGIN_SESSION_INFO, value: JSON.stringify({}) });
    }
  }

  triggerShowAdditionalContent() {
    this.showAdditionalContent = true;
  }

  processSms(message: string) {
    try {
      if (message) {
        const res = message.match('[0-9]{1,6}');

        if (res && res.length) {
          this.code.value = res[0];
          this.verifyCode();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.phone = params['phone'];
      if (Capacitor.isNativePlatform()) {
        const loginInfo = {
          phone: this.phone,
          page: 'verifysignup',
          singleUseRegistrationPassword: this.amplifyProvider.singleUseRegistrationPassword,
        }
        SecureStoragePlugin.set({ key: ONGOING_LOGIN_SESSION_INFO, value: JSON.stringify(loginInfo) });
      }
    });
    // this.smsRetriever.startWatching()
    //   .then(this.processSms.bind(this))
    //   .catch(e => {
    //     console.error(e);
    //   });
  }

  /*processSms(smsData) {
    try {
      const message = smsData.Message;
      if (message) {
        const res = message.match('[0-9]{1,6}');
        if (res && res.length) {
          this.verifyCodeForm.get('code').setValue(res[0]);
          this.verifyCode();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }*/

  ngAfterViewInit() {
    this.focusOnInput();
  }

  verifyCode(): void {
    this.analyticsService.tagButtonPress('verifysignup_page', 'verify_code');
    if (this.verifyCodeForm.valid) {
      this.submitForm();
    } else {
      this.alertProvider.showEmptyForm();
    }
  }

  submitForm() {
    this.loadingProvider.loadWhile(async stopLoading => {
      try {
        const currentUser = await this.amplifyProvider.verifySignUpCode(this.phone, this.verifyCodeForm.value.code);
        await this.contentService.setLocale('en');
      } catch (error) {
        console.error('Register: Verify code error', error);
        this.alertProvider.showAmplifyAuthError(error);
        this.registerSmsRetriever();
      } finally {
        setTimeout(() => {
          this.router.navigate(['/plan']);
          stopLoading();
        }, 2000)
      }
    });
  }

  resendCode() {
    this.analyticsService.tagButtonPress('verifysignup_page', 'resend_code');
    this.loadingProvider.loadWhile(stopLoading => {
      this.amplifyProvider.resendSignUpCode(this.phone)
        .then(() => {
          stopLoading();
          this.toastProvider.show(`We sent a SMS to ${this.phone} with a code, please enter it here.`);
        })
        .catch(error => {
          stopLoading();
          this.alertProvider.showAmplifyAuthError(error);
        });
    });
    this.showResendButton = false;
    const timeout = setTimeout(() => {
      this.showResendButton = true;
      clearTimeout(timeout);
    }, 1000 * 30);
  }

  focusOnInput() {
    setTimeout(() => {
      if (this.code) {
        this.code.setFocus();
      }
    }, 200);
  }

}
